import React from 'react';
import {
    BUSINESS_MODEL_OPTIONS,
    type RegulatorySettings,
} from '../settings/regulatorySettingsOptions';
export type { RegulatorySettings } from '../settings/regulatorySettingsOptions';

interface InstitutionSettingsProps {
    regulatorySettings: RegulatorySettings;
    onSettingsChange: (settings: RegulatorySettings) => void;
    onBack: () => void;
}

export default function InstitutionSettings({ regulatorySettings, onSettingsChange, onBack }: InstitutionSettingsProps) {
    return (
        <div className="h-full bg-bg flex flex-col">
            {/* Header */}
            <div className="border-b border-border-default px-6 py-4 flex items-center">
                <button
                    onClick={onBack}
                    className="flex items-center text-fg-tertiary hover:text-fg transition-colors mr-4"
                >
                    <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back
                </button>
                <div className="flex items-center">
                    <svg className="w-5 h-5 text-fg-tertiary mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <h1 className="text-lg font-semibold text-fg">Institution Settings</h1>
                </div>
            </div>

            {/* Temporarily disabled banner */}
            <div className="px-6 py-3 bg-accent-hover/60 border-b border-border-default flex items-center gap-2">
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-fg-secondary text-fg-secondary uppercase tracking-wide">Disabled</span>
                <span className="text-xs text-fg-tertiary">Settings are temporarily disabled while we integrate this feature.</span>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 opacity-50 pointer-events-none">
                <div className="max-w-xl mx-auto">
                    <p className="text-fg-tertiary mb-6 text-sm">
                        Configure your institution's regulatory framework settings. These settings help tailor research results to your specific context.
                    </p>

                    <div className="space-y-5">
                        {/* Regulatory approach */}
                        <div>
                            <label className="block text-sm font-medium text-fg mb-2">Regulatory approach</label>
                            <select
                                value={regulatorySettings.approach || ''}
                                onChange={(e) => onSettingsChange({ ...regulatorySettings, approach: e.target.value || undefined })}
                                className="w-full text-sm bg-accent-hover border border-border-default rounded-lg px-3 py-2 text-fg focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
                            >
                                <option value="">None selected</option>
                                <option value="standardised_approach">Standardised approach (SA)</option>
                                <option value="irb">Internal ratings-based (IRB)</option>
                            </select>
                            <p className="mt-1 text-xs text-fg-tertiary">Select the regulatory approach your institution uses for capital calculations.</p>
                        </div>

                        {/* Institution classification */}
                        <div>
                            <label className="block text-sm font-medium text-fg mb-2">Institution classification</label>
                            <select
                                value={regulatorySettings.institution_classification || ''}
                                onChange={(e) => onSettingsChange({ ...regulatorySettings, institution_classification: e.target.value || undefined })}
                                className="w-full text-sm bg-accent-hover border border-border-default rounded-lg px-3 py-2 text-fg focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
                            >
                                <option value="">None selected</option>
                                <option value="global_systemically_important_institution">Global systemically important institution (G-SII)</option>
                                <option value="other_systemically_important_institution">Other systemically important institution (O-SII)</option>
                                <option value="not_systemically_important">Not systemically important</option>
                            </select>
                            <p className="mt-1 text-xs text-fg-tertiary">Select your institution's systemic importance classification.</p>
                        </div>

                        {/* Supervisory authority */}
                        <div>
                            <label className="block text-sm font-medium text-fg mb-2">Supervisory authority</label>
                            <select
                                value={regulatorySettings.supervisory_authority || ''}
                                onChange={(e) => onSettingsChange({ ...regulatorySettings, supervisory_authority: e.target.value || undefined })}
                                className="w-full text-sm bg-accent-hover border border-border-default rounded-lg px-3 py-2 text-fg focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
                            >
                                <option value="">None selected</option>
                                <option value="ecb">European Central Bank (ECB)</option>
                                <option value="austria">Austrian Financial Market Authority</option>
                                <option value="belgium">National Bank of Belgium</option>
                                <option value="bulgaria">Bulgarian National Bank</option>
                                <option value="croatia">Croatian National Bank</option>
                                <option value="cyprus">Central Bank of Cyprus</option>
                                <option value="czech_republic">Czech National Bank</option>
                                <option value="denmark">Danish Financial Supervisory Authority</option>
                                <option value="estonia">Estonian Financial Supervisory Authority</option>
                                <option value="finland">Finnish Financial Supervisory Authority</option>
                                <option value="france">French Prudential Supervision and Resolution Authority</option>
                                <option value="germany">German Federal Financial Supervisory Authority (BaFin)</option>
                                <option value="greece">Bank of Greece</option>
                                <option value="hungary">Hungarian National Bank</option>
                                <option value="ireland">Central Bank of Ireland</option>
                                <option value="italy">Bank of Italy</option>
                                <option value="latvia">Financial and Capital Market Commission (Latvia)</option>
                                <option value="lithuania">Bank of Lithuania</option>
                                <option value="luxembourg">Commission de Surveillance du Secteur Financier</option>
                                <option value="malta">Malta Financial Services Authority</option>
                                <option value="netherlands">Dutch Central Bank</option>
                                <option value="poland">Polish Financial Supervision Authority</option>
                                <option value="portugal">Bank of Portugal</option>
                                <option value="romania">National Bank of Romania</option>
                                <option value="slovakia">National Bank of Slovakia</option>
                                <option value="slovenia">Bank of Slovenia</option>
                                <option value="spain">Bank of Spain</option>
                                <option value="sweden">Swedish Financial Supervisory Authority</option>
                            </select>
                            <p className="mt-1 text-xs text-fg-tertiary">Select your primary supervisory authority.</p>
                        </div>

                        {/* Business model focus */}
                        <div>
                            <label className="block text-sm font-medium text-fg mb-2">Business model focus</label>
                            <p className="mb-3 text-xs text-fg-tertiary">Select all business areas relevant to your institution.</p>
                            <div className="grid grid-cols-2 gap-2">
                                {BUSINESS_MODEL_OPTIONS.map(({ value, label }) => (
                                    <label key={value} className="flex items-center p-3 bg-accent-hover/50 border border-border-default rounded-lg hover:bg-accent-hover transition-colors cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={(regulatorySettings.business_model || []).includes(value)}
                                            onChange={(e) => {
                                                const current = regulatorySettings.business_model || [];
                                                const updated = e.target.checked
                                                    ? [...current, value]
                                                    : current.filter(model => model !== value);
                                                onSettingsChange({ ...regulatorySettings, business_model: updated.length > 0 ? updated : undefined });
                                            }}
                                            className="h-4 w-4 rounded border-border-default bg-fg-secondary text-accent focus:ring-accent focus:ring-offset-accent"
                                        />
                                        <span className="ml-2 text-sm text-fg-secondary">{label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Info Card */}
                    <div className="mt-6 bg-accent-hover/50 border border-border-default rounded-lg p-4">
                        <div className="flex">
                            <svg className="w-5 h-5 text-fg-tertiary mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                                <h4 className="text-sm font-medium text-fg">How these settings are used</h4>
                                <p className="mt-1 text-xs text-fg-tertiary">
                                    These settings help the AI assistant provide more relevant regulatory guidance tailored to your institution's specific context and requirements.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
